import { AutoAwesomeMosaic as AutoAwesomeMosaicIcon } from '@mui/icons-material';
import { FormHelperText, Typography } from '@mui/material';
import { first } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ALLOWED_FILE_EXTENSIONS_PRODUCT,
  ASSET_UPLOAD_SUPPORTED_FILE_TYPES_PRODUCT,
} from 'src/constants';
import { useGetCCAssets, useModal } from 'src/hooks';
import { IAsset, IProduct } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getAssetFromAssetId } from 'src/utils/gcf-helper';

import { AssetUploadModal } from '../modal/assetUploadModal/AssetUploadModal';
import { Image, StyledFileName, UploadImage } from './UploadImageButton.styled';

export const UploadImageButton = (props: {
  type?: string;
  handleValidateSelectAsset?: (groupImageName: string) => boolean;
}) => {
  const { formState, control, getValues } = useFormContext<
    IProduct & { asset?: File }
  >();
  const { data: assets = [], isLoading } = useGetCCAssets();
  const assetRecord = getAssetFromAssetId(assets, first(getValues().asset_id));
  const assetRules =
    props.type !== 'editProduct' ? { required: 'Required' } : {};

  const { isViewOnly } = useUserRole();
  const assetUploadModal = useModal();

  // Preview from the file's local blob so it survives the post-save reset and
  // never 404s. Kept out of the `asset` form field to avoid re-uploading.
  const [selectedImagePreview, setSelectedImagePreview] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const selectedImagePreviewUrlRef = useRef<string | null>(null);

  const showSelectedImagePreview = (file: File) => {
    if (selectedImagePreviewUrlRef.current) {
      URL.revokeObjectURL(selectedImagePreviewUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    selectedImagePreviewUrlRef.current = url;
    setSelectedImagePreview({ name: file.name, url });
  };

  useEffect(
    () => () => {
      if (selectedImagePreviewUrlRef.current) {
        URL.revokeObjectURL(selectedImagePreviewUrlRef.current);
      }
    },
    [],
  );

  const validateFile = (file: IAsset | File) => {
    const fileName = file instanceof File ? file.name : file.assetName;
    if (props.type === 'editProduct' && props?.handleValidateSelectAsset) {
      return props?.handleValidateSelectAsset(fileName);
    }

    return true;
  };

  return (
    <Controller
      rules={assetRules}
      name="asset"
      control={control}
      render={({ field }) => (
        <>
          <AssetUploadModal
            assetUploadModal={assetUploadModal}
            allowedFileExtension={ALLOWED_FILE_EXTENSIONS_PRODUCT}
            supportedFileType={ASSET_UPLOAD_SUPPORTED_FILE_TYPES_PRODUCT}
            onSelectAsset={(file: File) => {
              showSelectedImagePreview(file);
              field.onChange(file);
            }}
            validateFile={validateFile}
          />

          <UploadImage
            component="label"
            variant="text"
            disabled={isViewOnly}
            $hasError={Boolean(formState.errors.asset)}
            onClick={() => {
              assetUploadModal.onOpenModal();
            }}>
            {props.type !== 'editProduct' && !field.value ? (
              <>
                <AutoAwesomeMosaicIcon />
                <Typography>{lang.uploadProductImage}</Typography>
              </>
            ) : (
              ''
            )}

            {selectedImagePreview ? (
              <Image src={selectedImagePreview.url} alt="preview asset" />
            ) : assetRecord && !isLoading ? (
              <Image src={assetRecord?.previewUrl} alt="preview asset" />
            ) : (
              ''
            )}
          </UploadImage>

          <StyledFileName>
            {selectedImagePreview?.name ??
              (field.value instanceof File
                ? field.value.name
                : assetRecord?.name)}
          </StyledFileName>

          <FormHelperText error={Boolean(formState.errors.asset?.message)}>
            {formState.errors?.asset?.message}
          </FormHelperText>
        </>
      )}
    />
  );
};
